import { useState, useEffect } from 'react';
import {
	getAccountConfigOptions,
	updateAccountConfigOptions,
} from 'src/services/account';
import { ICatalog, ICatalogForm } from 'src/lib/schemas';
import authService from 'src/services/auth';

const useCatalogs = () => {
	const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const fetchCatalogs = async () => {
		// Only fetch catalogs if user is authenticated
		if (!authService.isAuthenticated()) {
			setIsLoading(false);
			return;
		}

		try {
			setIsLoading(true);
			const data = await getAccountConfigOptions<ICatalog>('catalogs');
			setCatalogs(data);
			setError(null);
		} catch (err) {
			setError(
				err instanceof Error ? err : new Error('Failed to fetch catalogs'),
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchCatalogs();

		// Listen for catalog updates from get_catalogs() function
		const handleCatalogsUpdate = () => {
			fetchCatalogs();
		};

		window.addEventListener('catalogs-updated', handleCatalogsUpdate);

		return () => {
			window.removeEventListener('catalogs-updated', handleCatalogsUpdate);
		};
	}, []);

	const getCatalogById = (id: string) => {
		// Search by both id (MongoDB _id) and catalogId (base64 encoded name)
		return catalogs.find((catalog) => catalog.id === id || catalog.catalogId === id);
	};

	const updateCatalog = async (payload: ICatalogForm) => {
		try {
			const updatedCatalog = await updateAccountConfigOptions(
				'catalogs',
				payload,
			);
			setCatalogs((prevCatalogs) =>
				prevCatalogs.map((catalog) =>
					catalog.id === updatedCatalog.id ? updatedCatalog : catalog,
				),
			);
			return updatedCatalog;
		} catch (err) {
			setError(
				err instanceof Error ? err : new Error('Failed to update catalog'),
			);
			throw err;
		}
	};

	return {
		catalogs,
		isLoading,
		error,
		getCatalogById,
		refetch: fetchCatalogs,
		updateCatalog,
	};
};

export default useCatalogs;
